import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { SecureStorage } from './storage/SecureStorage';
import { ApiError as AppApiError } from '@/types/residents';
import { AppConfig } from '@/constants/residents/config';
import { AuthEndpoints } from '../residents/api/endpoints/AuthEndpoints';
import { performTokenRefresh } from '@/services/shared/auth/refreshTokenHelper';
import { store } from '@/store';
import { logoutAndClear } from '@/services/shared/auth/logoutAndClear';

export class BaseAPI {
  protected api: AxiosInstance;
  private static instance: BaseAPI;
  private isRefreshingToken = false;
  private refreshTokenPromise: Promise<boolean> | null = null;

  protected constructor() {
    this.api = axios.create({
      baseURL: AppConfig.API.BASE_URL,
      timeout: AppConfig.API.TIMEOUT,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  static getInstance(): BaseAPI {
    if (!BaseAPI.instance) {
      BaseAPI.instance = new BaseAPI();
    }
    return BaseAPI.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.api.interceptors.request.use(
      async (config) => {
        // Don't add auth headers for authentication endpoints
        const isAuthEndpoint = config.url?.includes('/login') || 
                              config.url?.includes('/signup') || 
                              config.url?.includes('/forgot-password') ||
                              config.url?.includes('/users/set-password') ||
                              config.url?.includes('/users/verification');
        
        // Don't override existing Authorization header (for admin Basic Auth)
        const hasAuthHeader = config.headers?.Authorization;
        
        if (!isAuthEndpoint && !hasAuthHeader) {
          const token = await SecureStorage.getInstance().getToken();
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        
        // Handle both 401 (unauthorized) and 403 with "jwt expired" for consistency with RTK Query
        const shouldRefresh = error.response?.status === 401 || 
                             (error.response?.status === 403 && 
                              (error.response.data as any)?.error === 'jwt expired');
        
        // Prevent infinite refresh loops
        if (shouldRefresh && !originalRequest._isRetry) {
          
          try {
            // Use instance-level promise to prevent multiple simultaneous refreshes
            if (!this.refreshTokenPromise) {
              this.isRefreshingToken = true;
              this.refreshTokenPromise = performTokenRefresh(this.api);
            }
            
            const refreshed = await this.refreshTokenPromise;
            
            if (refreshed && originalRequest) {
              
              // Mark request as retry to prevent infinite loops
              originalRequest._isRetry = true;
              
              // Update Authorization header with new token
              const newToken = await SecureStorage.getInstance().getToken();
              if (newToken && originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              
              // Reset refresh state
              this.isRefreshingToken = false;
              this.refreshTokenPromise = null;
              
              // Retry original request
              return this.api.request(originalRequest);
            } else {
              console.error('🔴 BaseAPI: Token refresh failed, logging out');
              this.isRefreshingToken = false;
              this.refreshTokenPromise = null;
              
              // Refresh failed, logout user
              await this.handleLogout();
              
              // Return a specific error for failed refresh
              return Promise.reject(this.formatError({
                ...error,
                message: 'Session expired. Please log in again.'
              } as AxiosError));
            }
          } catch (refreshError) {
            console.error('🔴 BaseAPI: Token refresh error:', refreshError);
            this.isRefreshingToken = false;
            this.refreshTokenPromise = null;
            
            // Refresh failed, logout user
            await this.handleLogout();
            
            return Promise.reject(this.formatError({
              ...error,
              message: 'Session expired. Please log in again.'
            } as AxiosError));
          }
        }
        
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private async handleLogout(): Promise<void> {
    try {
      await logoutAndClear(store.dispatch);
    } catch (error) {
      console.error('🔴 BaseAPI: Error during logout:', error);
    }
  }

  private formatError(error: AxiosError): AppApiError {
    if (error.response) {
      // Server responded with error
      const data = error.response.data as any;
      return {
        code: data?.code || error.response.status.toString(),
        message: data?.message || error.message,
        details: data?.details,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
      };
    } else {
      // Something else happened
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unknown error occurred',
      };
    }
  }

  // Helper methods for API calls
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }
} 